import { For, Show } from "solid-js";
import type { Component } from "solid-js";
import { useShellStore } from "@ui/state/shell";
import { useBookingDetailQuery } from "../service";
import { CollapsibleSection } from "@ui/components/CollapsibleSection";
import { formatPhone } from "@utils/formatPhone";

const formatCurrency = (amount: number | null, currency: string | null): string => {
  if (amount === null || Number.isNaN(amount)) {
    return "-";
  }
  if (!currency) {
    return amount.toLocaleString();
  }
  try {
    return new Intl.NumberFormat(undefined, { style: "currency", currency }).format(amount);
  } catch (_error) {
    return `${currency} ${amount}`;
  }
};

const formatDateTime = (value: Date | null): string => (value ? value.toLocaleString() : "-");

const labelValue = (label: string, value: string | null | undefined) => (
  <div class="detail-field">
    <span class="detail-field__label">{label}</span>
    <span class="detail-field__value">{value ?? "-"}</span>
  </div>
);

export const BookingDetailPanel: Component = () => {
  const { state } = useShellStore();
  const bookingId = () => state.selectedBookingId;
  const query = useBookingDetailQuery(bookingId);

  const detail = () => query.data ?? null;
  const booking = () => detail()?.summary ?? null;
  const schedule = () => booking()?.schedule ?? null;
  const user = () => detail()?.user ?? null;
  const statusHistory = () => detail()?.statusHistory ?? [];

  const isLoading = () => Boolean(query.isLoading);
  const error = () => (query.error instanceof Error ? query.error : query.error ? new Error(String(query.error)) : null);

  return (
    <div class="chat-profile-panel">
      <Show when={bookingId()} fallback={<p>Select a booking to view details.</p>}>
        <Show when={!isLoading()} fallback={<p>Loading booking details...</p>}>
          <Show
            when={!error()}
            fallback={
              <p class="empty-state">
                Unable to load booking details right now.
                <Show when={error()?.message}>{(message) => <span class="error-detail">{message()}</span>}</Show>
              </p>
            }
          >
            <Show when={booking()} fallback={<p>Booking not found.</p>}>
              <CollapsibleSection title="BOOKING">
                {labelValue("Booking ID", booking()?.id ?? null)}
                {labelValue("Status", booking()?.status ?? null)}
                {labelValue("Service", booking()?.serviceId ?? "-")}
                {labelValue("Room type", booking()?.roomType ?? "-")}
                {labelValue("Source", booking()?.source ?? "-")}
                {labelValue("Created", formatDateTime(booking()?.createdAt ?? null))}
                {labelValue("Updated", formatDateTime(booking()?.updatedAt ?? null))}
              </CollapsibleSection>

              <CollapsibleSection title="SCHEDULE">
                {labelValue("Date", schedule()?.scheduledDate ?? null)}
                {labelValue("Time", schedule()?.timeSlotLabel ?? null)}
                {labelValue("Building", schedule()?.buildingId ?? null)}
                {labelValue("Floor", typeof schedule()?.floor === "number" ? String(schedule()?.floor) : null)}
                {labelValue("Apartment", schedule()?.apartmentId ?? null)}
                {labelValue("Notes", schedule()?.notes ?? null)}
              </CollapsibleSection>

              <CollapsibleSection title="PAYMENT">
                {labelValue("Amount", formatCurrency(booking()?.amount ?? null, booking()?.currency ?? null))}
                {labelValue("Currency", booking()?.currency ?? null)}
                {labelValue("Payment intent", booking()?.paymentIntentId ?? null)}
              </CollapsibleSection>

              <CollapsibleSection title="CUSTOMER">
                <Show when={user()} fallback={<p class="empty-state">No registered customer linked.</p>}>
                  {(person) => (
                    <div class="profile-contact">
                      {labelValue("Name", person().displayName)}
                      {labelValue("Email", person().email)}
                      {labelValue("Phone", formatPhone(person().phoneNumber))}
                    </div>
                  )}
                </Show>
              </CollapsibleSection>

              <CollapsibleSection title="STATUS HISTORY" defaultOpen={false}>
                <Show when={statusHistory().length > 0} fallback={<p class="empty-state">No status events recorded yet.</p>}>
                  <table class="status-history-table">
                    <thead>
                      <tr>
                        <th>Status</th>
                        <th>Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      <For each={statusHistory()}>
                        {(entry) => (
                          <tr>
                            <td>
                              <span class={`booking-status booking-status--${entry.status}`}>{entry.status}</span>
                            </td>
                            <td>{formatDateTime(entry.at)}</td>
                          </tr>
                        )}
                      </For>
                    </tbody>
                  </table>
                </Show>
              </CollapsibleSection>
            </Show>
          </Show>
        </Show>
      </Show>
    </div>
  );
};

